import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import SelectTsoPq, { Option } from '@components/Select/SelectTsoPq';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  StyledDeleteButton,
  StyledLineWrapper,
} from '@pages/Correlation/CorelationParameters/CorellationParametersSectionStyles';
import { StyledMeasurementDeviceTitle } from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import { TsosStateProps } from '@pages/PqIndex/Sections/PqIndexParameters';
import { useGetMeasurementDeviceGetAllQuery } from '@redux/apiSlices/measurementDeviceApi';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import {
  ApiCommonExternalDtoSubstationsDeviceHierarchyExternalDto,
  useLazyGetSubstationGetSubstationDeviceHierarchyQuery,
} from '@redux/apiSlices/substationApi';
import {
  ApiApplicationServicesDataTypesTrendTypesTrendResultsCount,
  useLazyGetTrendGetTrendResultsCountQuery,
} from '@redux/apiSlices/trendApi';
import { useDeleteTrendJobByIdMutation, useGetTrendJobGetAllQuery } from '@redux/apiSlices/trendJobApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppSelector } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import {
  ActiveTrendParametersProps,
  ActiveTrendProps,
  clearAllTrendChartData,
  DeviceDetails,
  selectActiveTrend,
  setActiveTrend,
  setActiveTrendName,
  setActiveTrendParameters,
  setDidUserApplyFilters,
  setIsFetchingTredData,
  setSelectedCells,
  setSelectedDeviceDetails,
  setTredData,
} from '@redux/slices/trendIdentificationSlice';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

const trendStatusMap = {
  error: [3],
  waiting: [0, 1],
};

const SubmittedCalculations = () => {
  const activeTrend = useAppSelector(selectActiveTrend);

  const [selectedTrend, setSelectedTrend] = useState<Option | null | undefined>(activeTrend);
  const [options, setOptions] = useState<Option[] | undefined>(undefined);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [deviceIdsMap, setDeviceIdsMap] = useState<{ [key: string]: string }>({});
  const [deviceHierarchies, setDeviceHierarchies] = useState<
    ApiCommonExternalDtoSubstationsDeviceHierarchyExternalDto[]
  >([]);

  const { data: trends } = useGetTrendJobGetAllQuery(undefined, {
    pollingInterval: 15000,
  });
  const [deleteTrend] = useDeleteTrendJobByIdMutation();
  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const [triggerGetTrendCount] = useLazyGetTrendGetTrendResultsCountQuery();
  const { data: measurementDevices } = useGetMeasurementDeviceGetAllQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const { data: tsos } = useGetTsoGetAllQuery();

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const deleteConfirmationMessage = selectedTrend
    ? `${t('trendIdentification.deleteDialogue.contentPart1')}${selectedTrend.label}${t('trendIdentification.deleteDialogue.contentPart2')}`
    : '';

  const getAllSelectedTrendValues = async () => {
    dispatch(setIsFetchingTredData(true));
    try {
      const response = await triggerGetTrendCount(
        {
          trendJobId: selectedTrend?.id,
          valueThreshold: 0,
          increaseThreshold: 0,
          increaseDurationThreshold: 0,
          averageIncreaseThreshold: 0.2,
        },
        true
      );
      dispatch(setTredData(response.data as ApiApplicationServicesDataTypesTrendTypesTrendResultsCount[]));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsFetchingTredData(false));
    }
  };

  const handleCalculationChange = (option: Option | null) => {
    dispatch(setDidUserApplyFilters(false));
    setSelectedTrend(option);
    dispatch(
      setActiveTrend(
        (option as ActiveTrendProps) ?? {
          disabled: false,
          id: Number.NaN,
          label: '',
          value: '',
          waiting: false,
        }
      )
    );
    dispatch(clearAllTrendChartData());
    dispatch(setSelectedCells([]));
  };

  const handleDeleteModal = () => {
    const selectedId = selectedTrend?.id;
    if (selectedId) setOpenModal(true);
  };

  const handleCalculationDelete = async () => {
    const selectedId = selectedTrend?.id;
    if (selectedId) {
      try {
        const response = await deleteTrend(selectedId);
        if (response.error) {
          throw new Error(`Error on trying to delete ${selectedId}`);
        }
        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('trendIdentification.snackbarDeleteSuccess'),
          })
        );
      } catch (error) {
        dispatch(
          setSnackbar({
            open: true,
            type: 'error',
            textContent: t('trendIdentification.snackbarDeleteError'),
          })
        );
      } finally {
        setSelectedTrend(undefined);
        dispatch(
          setActiveTrend({
            disabled: false,
            id: Number.NaN,
            label: '',
            value: '',
            waiting: false,
          })
        );
        dispatch(
          setActiveTrendParameters({
            activeTsos: [],
            startDate: '',
            endDate: '',
            measurmentParameters: [],
            measurmentDevices: {},
          })
        );
        setOpenModal(false);
      }
    }
  };

  useEffect(() => {
    const fetchedTrends = trends?.externalDtos ?? [];
    if (fetchedTrends.length > 0) {
      const proccesedCalculationsOptions = fetchedTrends
        .map((trend) => ({
          id: Number(trend.id),
          value: trend.name,
          label: trend.name,
          disabled: trendStatusMap.error.includes(trend.status),
          waiting: trendStatusMap.waiting.includes(trend.status),
        }))
        .sort((trendA, trendB) =>
          trendA.label.localeCompare(trendB.label, undefined, { numeric: true, sensitivity: 'base' })
        );

      setOptions(proccesedCalculationsOptions);
    }
  }, [trends]);

  useEffect(() => {
    if (!selectedTrend) return;

    const trendProps = trends?.externalDtos?.find((currentTrend) => currentTrend.id === selectedTrend.id);

    if (
      !trendProps?.start &&
      !trendProps?.end &&
      !trendProps?.measurementDeviceIds &&
      !trendProps?.measurementParameterIds
    )
      return;

    const devices =
      measurementDevices?.externalDtos?.filter((device) =>
        trendProps?.measurementDeviceIds.includes(device.id ?? -1)
      ) ?? [];

    const uniqueTsoIds = [...new Set(devices.map((device) => device.tsoId))];
    const parametersMap = new Map(measurementParameters?.externalDtos?.map((p) => [p.id, p.technicalName]));

    const mappedSelectedTsos: TsosStateProps[] = uniqueTsoIds.map((tsoId) => {
      const tsoData = tsos?.externalDtos?.find((tso: any) => tso.id === tsoId);
      return {
        id: Number(tsoId),
        name: tsoData?.name ?? '',
        active: true,
        abbreviation: tsoData?.abbreviation ?? '',
      };
    });

    const measurmentDevicesIdByTso = devices.reduce((acc, { id, tsoId }) => {
      if (!acc[tsoId]) acc[tsoId] = [];
      acc[tsoId].push(deviceIdsMap[id]);
      return acc;
    }, {});

    const parametersTechnicalNames: string[] = trendProps?.measurementParameterIds
      ?.map((id) => parametersMap.get(id))
      .filter(Boolean);

    const getDeviceDetails = () => {
      const map = {};

      const deviceIds: string[] = Object.values(measurmentDevicesIdByTso).flat() as string[];

      for (const deviceId of deviceIds) {
        if (!deviceId) return;
        const [substationId, measurementPointId, measurementDeviceId] = deviceId.split('-').map(Number);

        const substation = deviceHierarchies.find((substation) => substation.entityId === substationId);
        if (!substation) continue;

        const measurementPoint = substation.options?.find((option) => option.entityId === measurementPointId);
        if (!measurementPoint) continue;

        const measurementDevice = measurementPoint.options?.find((option) => option.entityId === measurementDeviceId);
        if (!measurementDevice) continue;

        map[`${measurementDeviceId}`] = {
          substationName: substation.label,
          measurementPointName: measurementPoint.label,
          measurementDeviceName: measurementDevice.label,
          tsoId: substation.tsoId,
        };
      }

      return map;
    };

    const activeTrendParameters: ActiveTrendParametersProps = {
      activeTsos: mappedSelectedTsos,
      startDate: trendProps?.start ?? '',
      endDate: trendProps?.end ?? '',
      measurmentParameters: parametersTechnicalNames,
      measurmentDevices: measurmentDevicesIdByTso,
    };

    const deviceDetails = getDeviceDetails();

    if (
      !Object.values(measurmentDevicesIdByTso)
        .flat()
        .includes((value) => value === undefined)
    ) {
      dispatch(setSelectedDeviceDetails(deviceDetails as { [key: string]: DeviceDetails }));
    }

    dispatch(setActiveTrendName(trendProps?.name ?? ''));
    dispatch(setActiveTrendParameters(activeTrendParameters));
    getAllSelectedTrendValues();
  }, [selectedTrend, trends, measurementDevices, measurementParameters, tsos, deviceHierarchies]);

  useEffect(() => {
    const tsoIds = tsos?.externalDtos?.map((tso) => tso.id);

    const getDevices = (nodes, level = 1, result = []) => {
      for (const node of nodes) {
        if (level === 3) {
          result.push(node);
          continue;
        }

        if (node.options && Array.isArray(node.options)) {
          getDevices(node.options, level + 1, result);
        }
      }
      return result;
    };

    const fetchHieracies = async () => {
      try {
        let deviceHierarcies: any[] = [];
        for (const id of tsoIds!) {
          const response = await triggerGetDeviceHierarchy(id, true);
          deviceHierarcies = [...deviceHierarcies, response.data?.externalDtos];
        }

        const devices = getDevices(deviceHierarcies.flat());
        setDeviceHierarchies(deviceHierarcies.flat());
        setDeviceIdsMap(
          devices.reduce((acc, { id, entityId }) => {
            acc[entityId] = id;
            return acc;
          }, {})
        );
      } catch (error) {
        console.error(error);
      }
    };

    fetchHieracies();
  }, []);

  useEffect(() => {
    setSelectedTrend(activeTrend);
  }, [activeTrend]);

  return (
    <>
      <StyledSectionWrapper>
        <StyledMeasurementDeviceTitle>
          {t('trendIdentification.sidePanel.submitedCalculations.title')}
        </StyledMeasurementDeviceTitle>
        <StyledLineWrapper>
          <SelectTsoPq
            options={options ?? []}
            label={t('trendIdentification.sidePanel.submitedCalculations.label')}
            value={selectedTrend}
            handleChange={handleCalculationChange}
            customwidth="27.375rem"
            placeholder={t('trendIdentification.fieldPlaceholder')}
            clearable
          />
          <PermissionGate roles={userOrSupportRestriction}>
            <StyledDeleteButton
              buttonType={selectedTrend?.id ? 'outlinedPrimary' : 'outlinedNeutral'}
              sizeVariant="m"
              icon={faTrash}
              onClick={handleDeleteModal}
              disabled={!selectedTrend?.id}
              tooltip={t('trendIdentification.deleteDialogue.tooltip')}
            />
            </PermissionGate>
        </StyledLineWrapper>
      </StyledSectionWrapper>
      {selectedTrend && (
        <ModalDialogTsoPq
          title={t('trendIdentification.deleteDialogue.title')}
          modalState={openModal}
          deleteConfirmationModal={true}
          confirmationText={deleteConfirmationMessage}
          onConfirm={() => handleCalculationDelete()}
          onCancel={() => setOpenModal(false)}
        />
      )}
    </>
  );
};

export default SubmittedCalculations;
